/**
 * A titled region of a page (cockatrice-ui `src/ui/Section.jsx`, ported to hyperscript; PLAN.md §0). The heading
 * labels the region (aria-labelledby).
 * `eyebrow` is an optional small ink-blue label above ("I.", "Privacy"); `description` sits under the title;
 * `actions` sit at the right of the title (below it on phones). `level`: heading level (default 2).
 * `size`: "md" (section title, sans 20) | "lg" (page title, serif 26).
 * `headingId`, `headingRef` and `headingFocusable` (PLAN.md §3, step 1) let a page name the heading, hold it, and move
 * the focus to it, as Settings does when a section is chosen in its SectionNav.
 */
import './css/section.css';
import { createUniqueId, mergeProps, splitProps } from 'solid-js';
import { dynamic, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

export interface SectionProps extends Attributes {
  /** Heading text; labels the region. */
  readonly title?: Child;
  /** Heading level. Default 2. */
  readonly level?: 1 | 2 | 3 | 4 | 5 | 6 | undefined;
  /** "md" sans section title or "lg" serif page title. Default "md". */
  readonly size?: 'md' | 'lg' | undefined;
  /** Small ink-blue label above the title, e.g. "I.". */
  readonly eyebrow?: Child;
  /** One line under the title. */
  readonly description?: Child;
  /** Buttons at the right of the title (below on phones). */
  readonly actions?: Child;
  /** Hairline and space above. */
  readonly divided?: boolean | undefined;
  /** The heading's id (otherwise a generated one); for a page whose links or tests name it. */
  readonly headingId?: string | undefined;
  /** Given the heading element once it is made: to scroll to it or move the focus there. */
  readonly headingRef?: ((heading: HTMLElement) => void) | undefined;
  /** The heading takes the focus from script (tabindex="-1"), never from Tab. */
  readonly headingFocusable?: boolean | undefined;
  /** Anchor id, for SectionNav. */
  readonly id?: string | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
  /** The content. */
  readonly children?: Child;
}

export default function Section(props: SectionProps): Child {
  const [local, rest] = splitProps(props, ['class', 'eyebrow', 'title', 'description', 'actions', 'children', 'level', 'size', 'id', 'divided', 'headingId', 'headingRef', 'headingFocusable']);
  const generated = createUniqueId();
  const hid = (): string => local.headingId ?? generated;
  const titled = (): boolean => Boolean(local.title);
  return el(
    'section',
    mergeProps(
      {
        id: () => local.id,
        class: () => ['ui-section', local.size === 'lg' && 'ui-section--lg', local.divided === true && 'ui-section--divided', local.class].filter(Boolean).join(' '),
        'aria-labelledby': () => (titled() ? hid() : undefined),
      },
      rest,
    ),
    when(
      () => Boolean(local.title) || Boolean(local.eyebrow) || Boolean(local.actions),
      () =>
        el(
          'div',
          { class: 'ui-section__head' },
          el(
            'div',
            { class: 'ui-section__heading' },
            when(
              () => Boolean(local.eyebrow),
              () => el('div', { class: 'ui-section__eyebrow' }, () => local.eyebrow),
            ),
            when(
              () => Boolean(local.title),
              () =>
                dynamic(
                  () => `h${local.level ?? 2}`,
                  {
                    id: hid,
                    class: 'ui-section__title',
                    tabIndex: () => (local.headingFocusable === true ? -1 : undefined),
                    ref: (heading: HTMLElement) => {
                      local.headingRef?.(heading);
                    },
                  },
                  () => local.title,
                ),
            ),
            when(
              () => Boolean(local.description),
              () => el('p', { class: 'ui-section__description' }, () => local.description),
            ),
          ),
          when(
            () => Boolean(local.actions),
            () => el('div', { class: 'ui-section__actions' }, () => local.actions),
          ),
        ),
    ),
    el('div', { class: 'ui-section__body' }, () => local.children),
  );
}
